import React, { useState, useEffect, useRef } from "react";
import { tokens, AGO } from "./tokens";
import { Sparkline } from "./Sparkline";
import { Popover } from "./Popover";
import { isProduction } from "../utils/envGate";

const t = tokens;

/* ═════════════════════════════════════════════════════════════
   Section — groups related tiles with a clear heading
═════════════════════════════════════════════════════════════ */
export function Section({ title, subtitle, accent = t.brand.base, children, tools = null, dense = false }) {
  return (
    <section style={{ marginBottom: dense ? t.space.xl : t.space["2xl"] }}>
      {title && (
        <div style={{
          display: "flex", alignItems: "flex-end", justifyContent: "space-between",
          marginBottom: t.space.lg, paddingLeft: 0, gap: 16,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Tile-style accent */}
            <div style={{
              width: 3.5, height: 28, borderRadius: 2,
              background: `linear-gradient(180deg, ${accent} 0%, ${accent}88 100%)`,
              boxShadow: `0 0 14px ${accent}80`,
              flexShrink: 0,
            }} />
            <div>
              <div style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: accent,
                display: "flex", alignItems: "center", gap: 10, lineHeight: 1,
              }}>
                {title}
              </div>
              {subtitle && (
                <div style={{
                  fontSize: 12.5, color: t.text.secondary,
                  marginTop: 6, fontWeight: 500, letterSpacing: "-0.005em",
                }}>{subtitle}</div>
              )}
            </div>
          </div>
          {tools && <div style={{ flexShrink: 0 }}>{tools}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/* ═════════════════════════════════════════════════════════════
   Grid — responsive tile grid with staggered fade-up
═════════════════════════════════════════════════════════════ */
export function Grid({ cols = 4, gap = t.space.lg, children }) {
  const items = React.Children.toArray(children);
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${cols === 2 ? 320 : cols === 3 ? 260 : 220}px, 1fr))`,
      gap,
    }}>
      {items.map((child, i) => (
        <div key={i} style={{
          animation: `tileIn 380ms cubic-bezier(0.2,0.8,0.2,1) both`,
          animationDelay: `${Math.min(i * 40, 320)}ms`,
        }}>{child}</div>
      ))}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════
   Number count-up animation hook
═════════════════════════════════════════════════════════════ */
function useCountUp(target, duration = 800) {
  const [display, setDisplay] = useState(target);
  const startRef = useRef(target);
  const startTimeRef = useRef(null);
  useEffect(() => {
    // Only animate numbers; skip strings like "₹4.2K" (we still display them).
    const numTarget = typeof target === "number" ? target : NaN;
    if (isNaN(numTarget) || Math.abs(numTarget - (typeof display === "number" ? display : 0)) < 0.01) {
      setDisplay(target);
      return;
    }
    const from = typeof display === "number" ? display : 0;
    startRef.current = from;
    startTimeRef.current = null;
    let frame;
    const step = (ts) => {
      if (startTimeRef.current == null) startTimeRef.current = ts;
      const t = Math.min(1, (ts - startTimeRef.current) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (numTarget - from) * eased);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => frame && cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);
  return display;
}

/* ═════════════════════════════════════════════════════════════
   Tile — the atomic metric card
═════════════════════════════════════════════════════════════ */
export function Tile({
  label,
  value,                // string or number; strings are shown as-is, numbers animate
  sub,
  delta,                // { value: 8.3, direction: "up"|"down", period: "vs prev" }
  status,               // "good" | "warn" | "critical" | null
  variant = "standard", // "hero" | "standard" | "mini"
  icon = null,
  accent,               // side-rail color
  gradient,             // one of Object.keys(tokens.gradients) — only hero
  source,               // {db, formula, updated_at}
  loading = false,
  error = null,
  onClick,
  sparkline,            // array of numbers
  sparklineColor,
  children,
}) {
  const [hover, setHover] = useState(false);
  const [showProv, setShowProv] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const triggerRef = useRef(null);
  const valSize = variant === "hero" ? t.font.heroValue.size
                : variant === "mini" ? 20
                : t.font.bigValue.size;
  const statusColor = status ? t.status[status] : t.text.primary;
  const isActionable = !!onClick && !loading && !error;

  // Only count-up numeric values
  const numericValue = typeof value === "number" ? value : null;
  const animated = useCountUp(numericValue ?? value);

  // Pretty display for animated number (strip decimals if input was integer-ish)
  const displayValue = typeof value === "number"
    ? (Number.isInteger(value) ? Math.round(animated).toLocaleString("en-IN") : Number(animated).toFixed(1))
    : value;

  const bg = variant === "hero" && gradient && t.gradients[gradient]
    ? t.gradients[gradient]
    : t.surface.card;

  return (
    <div
      onClick={isActionable ? onClick : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      role={isActionable ? "button" : undefined}
      tabIndex={isActionable ? 0 : undefined}
      style={{
        position: "relative",
        background: bg,
        borderRadius: t.radius.lg,
        padding: variant === "hero" ? "22px 22px 18px" : "18px 18px 16px",
        cursor: isActionable ? "pointer" : "default",
        transition: `transform ${t.transition.slow}, box-shadow ${t.transition.slow}, background ${t.transition.base}`,
        minHeight: variant === "hero" ? 168 : variant === "mini" ? 80 : 120,
        display: "flex", flexDirection: "column",
        border: `1px solid ${hover && isActionable ? "rgba(148,163,184,0.18)" : "transparent"}`,
        boxShadow: hover && isActionable ? t.shadow.hover : t.shadow.sm,
        transform: hover && isActionable ? "translateY(-2px)" : "translateY(0)",
        overflow: "hidden",
      }}>
      {/* Decorative corner shine for hero tiles */}
      {variant === "hero" && (
        <div style={{
          position: "absolute", top: -40, right: -40, width: 140, height: 140,
          background: `radial-gradient(circle at center, ${(accent || t.brand.base)}18 0%, transparent 65%)`,
          pointerEvents: "none",
        }} />
      )}

      {/* Accent rail (optional) */}
      {accent && variant !== "hero" && (
        <div style={{
          position: "absolute", left: 0, top: 14, bottom: 14,
          width: 3, borderRadius: "0 2px 2px 0", background: accent, opacity: 0.7,
          boxShadow: `0 0 12px ${accent}66`,
        }} />
      )}

      {/* Header: label + icon + provenance */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 8, marginBottom: t.space.md,
      }}>
        <span style={{
          fontSize: t.font.label.size,
          fontWeight: t.font.label.weight,
          letterSpacing: t.font.label.spacing,
          textTransform: "uppercase",
          color: t.text.muted,
          lineHeight: 1,
        }}>{label}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {source && (
            <button
              ref={triggerRef}
              data-fc-prov
              className="fc-tap-target-44"
              onClick={(e) => {
                e.stopPropagation();
                const next = !showProv;
                if (next && triggerRef.current) {
                  setAnchorRect(triggerRef.current.getBoundingClientRect());
                }
                setShowProv(next);
              }}
              title="Click to see source + formula"
              aria-label="Show data source"
              style={{
                width: 24, height: 24, borderRadius: 7, display: "grid", placeItems: "center",
                color: showProv ? "#FFF" : "#CBD5E1",
                cursor: "pointer", fontSize: 12, fontWeight: 800,
                background: showProv ? t.brand.base : "#1E293B",
                border: `1px solid ${showProv ? t.brand.base : "#334155"}`,
                transition: "all 0.15s", padding: 0, fontFamily: "inherit",
                boxShadow: showProv ? `0 0 0 3px ${t.brand.base}30` : "none",
              }}
              onMouseEnter={(e) => { if (!showProv) { e.currentTarget.style.background = "#334155"; e.currentTarget.style.color = "#FFF"; }}}
              onMouseLeave={(e) => { if (!showProv) { e.currentTarget.style.background = "#1E293B"; e.currentTarget.style.color = "#CBD5E1"; }}}>
              i
            </button>
          )}
          {icon}
        </div>
      </div>

      {/* Value */}
      {loading ? (
        <div style={{
          height: valSize, width: "60%",
          background: "linear-gradient(90deg, rgba(148,163,184,0.08), rgba(148,163,184,0.16), rgba(148,163,184,0.08))",
          backgroundSize: "200% 100%", animation: "shimmer 1.2s infinite",
          borderRadius: 4,
        }} />
      ) : error ? (
        <div style={{
          fontSize: 13, color: t.status.warn, lineHeight: 1.3,
          padding: "6px 0", display: "flex", gap: 6, alignItems: "flex-start",
        }}>
          <span>⚠</span>
          <span style={{ color: t.text.secondary }}>{error}</span>
        </div>
      ) : (
        <div style={{
          display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap",
        }}>
          <span style={{
            fontSize: valSize,
            fontWeight: 800,
            letterSpacing: "-0.02em",
            color: statusColor,
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
            textShadow: variant === "hero" ? `0 2px 20px ${(accent || t.brand.base)}18` : undefined,
          }}>{displayValue}</span>
          {delta && <DeltaChip {...delta} />}
        </div>
      )}

      {/* Sub text */}
      {!loading && !error && sub && (
        <div style={{
          fontSize: t.font.subtitle.size, color: t.text.secondary,
          marginTop: t.space.sm, lineHeight: 1.4,
        }}>{sub}</div>
      )}

      {/* Sparkline */}
      {!loading && !error && Array.isArray(sparkline) && sparkline.length > 1 && (
        <div style={{ marginTop: "auto", paddingTop: t.space.sm, opacity: 0.8 }}>
          <Sparkline data={sparkline} color={sparklineColor || accent || t.brand.base} />
        </div>
      )}

      {children && !loading && !error && (
        <div style={{ marginTop: "auto", paddingTop: t.space.md }}>{children}</div>
      )}

      <Popover
        open={showProv && !!source}
        anchorRect={anchorRect}
        onRequestClose={() => setShowProv(false)}>
        <div style={{
          background: "#0F172A",
          border: `2px solid ${t.brand.base}`,
          borderRadius: 12, padding: "16px 18px",
          boxShadow: `0 24px 60px rgba(0,0,0,0.7), 0 0 0 4px ${t.brand.base}20`,
          fontSize: 12, color: "#E2E8F0", lineHeight: 1.55,
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
            paddingBottom: 8, borderBottom: `1px solid ${t.surface.divider}`,
          }}>
            <div style={{
              width: 20, height: 20, borderRadius: 5, background: t.brand.base,
              display: "grid", placeItems: "center", fontSize: 11, fontWeight: 800, color: "#FFF",
            }}>i</div>
            <span style={{
              fontWeight: 800, color: "#F1F5F9", fontSize: 11,
              textTransform: "uppercase", letterSpacing: "0.08em",
            }}>Data Source</span>
          </div>

          {/* Redaction: hide db + formula in production (fail closed) */}
          {!isProduction() && source?.db && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em",
                color: t.text.muted, textTransform: "uppercase", marginBottom: 3 }}>From</div>
              <div style={{ fontFamily: t.font.family_mono, fontSize: 11, color: "#F1F5F9",
                padding: "4px 8px", background: "rgba(15,23,42,0.7)",
                border: `1px solid ${t.surface.divider}`, borderRadius: 5,
                display: "inline-block" }}>{source.db}</div>
            </div>
          )}
          {!isProduction() && source?.formula && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em",
                color: t.text.muted, textTransform: "uppercase", marginBottom: 3 }}>Formula</div>
              <div style={{ fontFamily: t.font.family_mono, fontSize: 11, color: "#E2E8F0",
                padding: "8px 10px", background: "rgba(0,0,0,0.3)",
                border: `1px solid ${t.surface.divider}`, borderRadius: 5,
                lineHeight: 1.5, wordBreak: "break-word" }}>{source.formula}</div>
            </div>
          )}
          {source?.scope && (
            <div style={{ fontSize: 11, color: t.text.secondary, marginBottom: 8 }}>
              Scope: {source.scope}
            </div>
          )}
          {source?.updated_at && (
            <div style={{ fontSize: 10, color: t.text.muted,
              display: "flex", alignItems: "center", gap: 5, marginTop: 10 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.status.info,
                boxShadow: `0 0 6px ${t.status.info}` }} />
              Refreshed {AGO(source.updated_at) || "just now"}
            </div>
          )}
          {isProduction() && (
            <div style={{ fontSize: 10, color: t.text.muted, marginTop: 6 }}>
              Source details hidden in production.
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════
   DeltaChip — styled change badge
═════════════════════════════════════════════════════════════ */
function DeltaChip({ value, direction, period }) {
  const up = direction === "up";
  const neu = direction !== "up" && direction !== "down";
  const col = up ? t.status.good : neu ? t.text.muted : t.status.critical;
  const bg  = up ? t.status.goodBg : neu ? "rgba(100,116,139,0.12)" : t.status.criticalBg;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 8px 3px 6px", borderRadius: t.radius.pill,
      background: bg, color: col,
      fontSize: 11, fontWeight: 700, letterSpacing: "0.02em",
    }}>
      <span style={{ fontSize: 12, lineHeight: 1 }}>{up ? "↑" : neu ? "→" : "↓"}</span>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{Math.abs(Number(value||0)).toFixed(1)}%</span>
      {period && <span style={{ color: t.text.muted, fontWeight: 500, marginLeft: 2 }}>{period}</span>}
    </span>
  );
}

/* ═════════════════════════════════════════════════════════════
   FreshnessBadge
═════════════════════════════════════════════════════════════ */
export function FreshnessBadge({ updated_at }) {
  if (!updated_at) return null;
  const diffMin = (Date.now() - new Date(updated_at).getTime()) / 60000;
  let color, label;
  if (diffMin < 5)    { color = t.status.good;    label = "Live"; }
  else if (diffMin < 60)  { color = t.status.info;  label = "Fresh"; }
  else if (diffMin < 1440){ color = t.status.warn;  label = "Hourly"; }
  else                { color = t.status.critical; label = "Stale"; }
  return (
    <span title={`Updated ${AGO(updated_at)}`} style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      fontSize: 10, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase",
      color, padding: "2px 8px", borderRadius: t.radius.pill,
      background: color.replace("rgb", "rgba").replace(")", ", 0.12)"),
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: color }} />
      {label}
    </span>
  );
}

/* ═════════════════════════════════════════════════════════════
   DetailDrawer
═════════════════════════════════════════════════════════════ */
export function DetailDrawer({ open, onClose, title, subtitle, children, width = 560 }) {
  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
        zIndex: 100, backdropFilter: "blur(6px)",
        animation: "fadeIn 180ms",
      }} />
      <div style={{
        position: "fixed", top: 0, right: 0, height: "100vh", width,
        maxWidth: "92vw",
        background: `linear-gradient(180deg, rgba(24,32,50,0.96) 0%, rgba(14,20,32,0.96) 100%)`,
        borderLeft: `1px solid ${t.surface.divider}`,
        boxShadow: t.shadow.lg, zIndex: 101,
        display: "flex", flexDirection: "column",
        animation: "slideIn 260ms cubic-bezier(0.2,0.8,0.2,1)",
      }}>
        <div style={{
          padding: "20px 24px 16px", borderBottom: `1px solid ${t.surface.divider}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          backdropFilter: "blur(10px)",
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: t.text.primary, letterSpacing: "-0.01em" }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: t.text.muted, marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} aria-label="Close drawer" style={{
            width: 32, height: 32, borderRadius: 8, border: "none", cursor: "pointer",
            background: "rgba(148,163,184,0.08)", color: t.text.secondary, fontSize: 16,
            transition: `all ${t.transition.fast}`,
          }}>✕</button>
        </div>
        <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>{children}</div>
      </div>
    </>
  );
}

/* ═════════════════════════════════════════════════════════════
   Expandable
═════════════════════════════════════════════════════════════ */
export function Expandable({ label = "Show detail", defaultOpen = false, children, accent }) {
  const [open, setOpen] = useState(defaultOpen);
  const color = accent || t.brand.base;
  return (
    <div style={{ marginTop: t.space.lg }}>
      <button onClick={() => setOpen(!open)} style={{
        background: open
          ? `linear-gradient(135deg, ${color}30 0%, ${color}12 100%)`
          : "#1A2233",
        border: `1.5px solid ${open ? color : "#283447"}`,
        color: open ? "#FFFFFF" : "#E2E8F0",
        fontSize: 13, fontWeight: 700, letterSpacing: "-0.005em",
        padding: "10px 18px", borderRadius: 10, cursor: "pointer",
        display: "inline-flex", alignItems: "center", gap: 10,
        transition: "all 0.15s",
        boxShadow: open ? `0 4px 12px ${color}35` : "none",
        fontFamily: "inherit",
      }}
      onMouseEnter={e => { if (!open) { e.currentTarget.style.background = "#22304A"; e.currentTarget.style.borderColor = color + "60"; }}}
      onMouseLeave={e => { if (!open) { e.currentTarget.style.background = "#1A2233"; e.currentTarget.style.borderColor = "#283447"; }}}>
        {/* Icon dot */}
        <span style={{ width: 8, height: 8, borderRadius: "50%",
          background: open ? "#FFFFFF" : color,
          boxShadow: open ? "0 0 8px rgba(255,255,255,0.6)" : `0 0 6px ${color}`,
          transition: "all 0.15s" }}/>
        {open ? "Hide detail" : label}
        <span style={{ fontSize: 14, transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 200ms", display: "inline-block",
          color: open ? "#FFFFFF" : color }}>⌄</span>
      </button>
      {open && (
        <div style={{ marginTop: t.space.lg, animation: "fadeUp 260ms cubic-bezier(0.2,0.8,0.2,1)",
          paddingLeft: 16, borderLeft: `2px solid ${color}40` }}>
          {children}
        </div>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════
   HealthDot — glowing pulse for "live"
═════════════════════════════════════════════════════════════ */
export function HealthDot({ status = "good", label, sub }) {
  const c = t.status[status];
  const pulse = status === "good" || status === "info";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "12px 16px", borderRadius: t.radius.md,
      background: t.surface.card,
      border: `1px solid ${t.surface.divider}`,
      transition: `all ${t.transition.base}`,
    }}>
      <span style={{ position: "relative", width: 10, height: 10, flexShrink: 0 }}>
        <span style={{
          position: "absolute", inset: 1, borderRadius: 4, background: c,
          boxShadow: `0 0 0 3px ${c}22`,
        }} />
        {pulse && (
          <span style={{
            position: "absolute", inset: 0, borderRadius: 5, background: c,
            opacity: 0.5, animation: "halo 2.6s ease-out infinite",
          }} />
        )}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: t.text.primary, letterSpacing: "-0.005em" }}>{label}</div>
        {sub && <div style={{
          fontSize: 11, color: t.text.muted, marginTop: 2,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>{sub}</div>}
      </div>
    </div>
  );
}

/* Inject keyframes + scrollbar once */
if (typeof document !== "undefined" && !document.getElementById("fancall-keyframes-v2")) {
  const s = document.createElement("style");
  s.id = "fancall-keyframes-v2";
  s.innerHTML = `
    @keyframes shimmer { 0% {background-position:200% 0} 100% {background-position:-200% 0} }
    @keyframes fadeIn  { from {opacity:0} to {opacity:1} }
    @keyframes fadeUp  { from {opacity:0; transform:translateY(6px)} to {opacity:1; transform:translateY(0)} }
    @keyframes slideIn { from {transform:translateX(16px);opacity:0} to {transform:translateX(0);opacity:1} }
    @keyframes tileIn  { from {opacity:0; transform:translateY(10px) scale(0.985)} to {opacity:1; transform:translateY(0) scale(1)} }
    @keyframes halo    { 0% {transform:scale(1); opacity:0.6} 80% {transform:scale(3.8); opacity:0} 100% {transform:scale(3.8); opacity:0} }
    @keyframes pulse   { 0%,100% {opacity:1} 50% {opacity:0.55} }

    /* Custom scrollbar */
    *::-webkit-scrollbar { width: 10px; height: 10px; }
    *::-webkit-scrollbar-track { background: transparent; }
    *::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.18); border-radius: 5px; border: 2px solid transparent; background-clip: padding-box; }
    *::-webkit-scrollbar-thumb:hover { background: rgba(148,163,184,0.32); border: 2px solid transparent; background-clip: padding-box; }

    /* Focus ring — keyboard-only, crisp */
    *:focus { outline: none; }
    *:focus-visible { outline: 2px solid rgba(255,138,28,0.6); outline-offset: 2px; border-radius: 6px; }

    /* Remove number input arrows for cleaner look */
    input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  `;
  document.head.appendChild(s);
}
